import { useEnableVideoBackgroundFeatureMutation } from '../../../apps/admin/routes/WebsiteDesignerV2/WebsiteDesigner.graphql.generated';
import { EventWebsiteGuestDocument } from '../../../apps/guest/GuestSite.graphql.generated';
import { BasicEventInfoDocument } from '../../utils/eventInfo/basicEventInfo.graphql.generated';
import { GetWebsiteDesignDocument } from '@graphql/queries/websiteDesign.graphql.generated';
import { useTranslation } from '@shared/core';
import { useEventInfo } from '@shared/utils/eventInfo';
import { addError } from '@shared/utils/logger';
import { previewMessageBusSubject } from '@shared/utils/previewMessageBus';
import { useToast } from '@withjoy/joykit';
import { useCallback, useEffect, useState } from 'react';

interface UseHeaderLayoutUpgradeDialogControllerParams extends Readonly<{ isOpen: boolean }> {}

export const useHeaderLayoutUpgradeDialogController = (params: UseHeaderLayoutUpgradeDialogControllerParams) => {
  const { isOpen } = params;
  const { tq } = useTranslation('headerLayoutUpgrade');
  const { eventInfo } = useEventInfo();
  const [screen, setScreen] = useState<'offer' | 'success'>('offer');
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      setScreen('offer');
    }
  }, [isOpen, setScreen]);

  const [enableVideoBackgroundFeature, { loading: isEnablingVideoBackgroundFeature }] = useEnableVideoBackgroundFeatureMutation();

  const handleUpgradeClick = useCallback(async () => {
    const eventId = eventInfo?.eventId;

    if (!eventId) {
      return;
    }

    try {
      await enableVideoBackgroundFeature({
        variables: {
          eventId,
        },
        refetchQueries: [EventWebsiteGuestDocument, GetWebsiteDesignDocument, BasicEventInfoDocument],
        awaitRefetchQueries: true,
      });

      // Trigger refetch in iframe to get fresh feature flags
      previewMessageBusSubject.next({
        type: 'sendMessage',
        payload: {
          message: {
            action: 'adminEventPageChange',
            source: 'joy',
            value: 'welcome',
          },
        },
      });

      setScreen('success');
    } catch (error) {
      addError(error, {
        context: 'useHeaderLayoutUpgradeDialogController#enableVideoBackgroundFeature',
        eventId,
      });
      toast(tq`Something went wrong. Please try again later.`);
    }
  }, [eventInfo?.eventId, enableVideoBackgroundFeature, toast, tq]);

  return {
    screen,
    isUpgrading: isEnablingVideoBackgroundFeature,
    handleUpgradeClick,
  };
};
